"use client";
import {
  FontAwesomeIcon,
  useMediaQuery,
  VeBox,
  VeTypography,
} from "@packages/ui/mui";
import Upload from "../Upload/Upload";
import { useEffect, useState } from "react";
import { faAngleUp, faCaretUp } from "@fortawesome/free-solid-svg-icons";
import { InfoImg, UploadDocumentCard, VeTooltip } from "..";

interface FileInfo {
  s3ObjectKey: string;
  fileSize: number;
  status: "ACTIVE" | "IN_ACTIVE";
  originalFileName: string;
  commentStatus?: string;
  comment?: string;
  objectKey?: string;
  docType?: string;
  url?: string;
}

type attachMentProps = {
  data?: any;
  useSession: any;
  icon: any;
  uploadFiles: any;
  title?: any;
  description?: any;
  required?: boolean;
  filesSelected?: any[];
  bucketType: any;
  setFileSelected?: any;
  index?: any;
  item?: any;
  image: any;
  edit?: boolean;
  noPadding?: boolean;
  fileTypeImages: any;
  companyProfile?: boolean;
  minHeight?: string;
  mutliUpload?: boolean;
  error?: boolean;
  acceptedTypes?: any;
  validationErrors?: any[];
  softDelete?: boolean; // Does not remove the document , but just makes the file status in_active
  setLoading?: any; // send the update state function to capture loading state of file upload.
  admin?: boolean;
  useQueryData?: any;
  setFileKey?: any;
  id?: string;
  isShareholder?: boolean;
  showInActiveFiles?: boolean;
  docType?: string;
  showError?: boolean;
  noMargin?: any;
  smallIcon?: boolean;
  belowTitle?: string;
  sizeRequired?: boolean;
  maxSize?: number;
  displayFiles?: boolean;
  kmlFileType?: boolean;
  fileSizeRequired?: boolean;
  downloadText?: boolean;
  showUploadedCard?: boolean;
  deleteFile?: boolean;
  errorText?: string;
  downloadOntextClick?: boolean;
  hideUploadAfterSuccess?: boolean;
  showDeleteConfirmationModel?: boolean;
  wrapDocumentCards?: boolean;
  cmsUpload?: boolean;
  toolTipDescription?: any;
  deleteSuccess?: () => void;
  noPermission?: boolean;
};

export function CommonUpload({
  useSession,
  icon,
  uploadFiles,
  title,
  description,
  required,
  filesSelected = [],
  bucketType,
  setFileSelected,
  index,
  item,
  image,
  edit = true,
  fileTypeImages,
  noPadding = false,
  mutliUpload = true,
  minHeight = "190px",
  error = false,
  acceptedTypes,
  validationErrors,
  softDelete = false,
  setLoading,
  admin = false,
  setFileKey,
  data,
  id,
  isShareholder = false,
  showInActiveFiles = true,
  docType = "",
  showError = false,
  noMargin = false,
  smallIcon = false,
  belowTitle,
  sizeRequired = true,
  maxSize = 250,
  displayFiles = true,
  kmlFileType = false,
  fileSizeRequired = true,
  downloadText = false,
  showUploadedCard = true,
  deleteFile = true,
  errorText = "",
  downloadOntextClick = false,
  hideUploadAfterSuccess = false,
  showDeleteConfirmationModel = false,
  wrapDocumentCards = false,
  cmsUpload = false,
  toolTipDescription,
  noPermission = false,
  deleteSuccess = () => {},
}: attachMentProps) {
  const [showAll, setShowAll] = useState(false);
  const [flatFilesSelected, setFlatFilesSelected] = useState<FileInfo[]>([]);
  const [allFileSelected, setAllFileSelected] = useState<FileInfo[]>([]);
  const isMobile = useMediaQuery("(max-width: 600px)");

  useEffect(() => {
    if (Array.isArray(filesSelected)) {
      let flatFiles = filesSelected.flat(Infinity);

      // Filter out IN_ACTIVE files if showInActiveFiles is false
      if (!showInActiveFiles) {
        flatFiles = flatFiles.filter((ele) => ele.status !== "IN_ACTIVE");
      }

      // Only update the state if the flattened and filtered array is different
      if (JSON.stringify(flatFiles) !== JSON.stringify(flatFilesSelected)) {
        setFlatFilesSelected(flatFiles);
      }
    } else {
      if (filesSelected) {
        setFlatFilesSelected([filesSelected]);
      } else setFlatFilesSelected([]);
    }
  }, [filesSelected, showInActiveFiles]);

  useEffect(() => {
    if (filesSelected?.length > 0 && allFileSelected?.length === 0)
      setAllFileSelected(filesSelected);
  }, [filesSelected]);

  const fileId = item ? item.id : id;

  return (
    <VeBox
      padding={noPadding ? "0px" : "16px 16px 16px 0"}
      bgcolor={"white"}
      sx={{ lineHeight: "16px" }}
      border={error ? "1px solid red" : "none"}
      borderRadius={error ? "8px" : 0}
      display={"flex"}
      minHeight={edit && minHeight ? minHeight : "max-content"}
      flexDirection={"column"}
      justifyContent={"space-between"}
    >
      <VeBox>
        {title && (
          <VeBox sx={{ display: "flex", gap: "4px" }}>
            <VeTypography variant="label3Secondary">
              {title}
              {required && (
                <span style={{ color: "red" }}>
                  *<br />
                </span>
              )}
            </VeTypography>
            {toolTipDescription && (
              <VeTooltip
                title={
                  <VeTypography
                    sx={{
                      padding: "4px 8px",
                      display: "flex",
                      maxWidth: "300px",
                    }}
                    variant="label4MainWhite"
                  >
                    {toolTipDescription}
                  </VeTypography>
                }
                placement="bottom"
                arrow
              >
                <VeBox sx={{ cursor: "pointer", display: "flex" }}>
                  <InfoImg small={true} />
                </VeBox>
              </VeTooltip>
            )}
          </VeBox>
        )}
        {description && (
          <VeTypography variant="label3Tertiary">{description}</VeTypography>
        )}
      </VeBox>
      {edit && !hideUploadAfterSuccess && (
        <VeBox marginTop={noMargin ? "" : "auto"}>
          <Upload
            noPermission={noPermission}
            icon={icon}
            useSession={useSession}
            maxSize={maxSize || 250}
            multiple={mutliUpload}
            uploadFiles={uploadFiles}
            setUpload={setFileSelected}
            bucketType={bucketType}
            image={image}
            idDocSubType={item?.idDocSubType}
            docType={docType?.length > 0 ? docType : item?.docType}
            fileTypeImages={fileTypeImages}
            index={index}
            setFileUploading={setLoading}
            acceptTypes={[...acceptedTypes]}
            highlightError={validationErrors?.includes(fileId)}
            smallIcon={smallIcon}
            belowTitle={belowTitle}
            sizeRequired={sizeRequired}
            noMargin={noMargin}
            kmlFileType={kmlFileType}
            errorText={errorText}
            cmsUpload={cmsUpload}
          />
        </VeBox>
      )}

      {showUploadedCard && flatFilesSelected?.length > 0 && displayFiles && (
        <VeBox
          marginTop={"8px"}
          display={"flex"}
          flexDirection={wrapDocumentCards ? "row" : "column"}
          flexWrap={wrapDocumentCards ? "wrap" : "nowrap"}
          gap={"8px"}
          height={
            showAll
              ? "min-content"
              : flatFilesSelected?.length > 3
                ? "125px"
                : "max-content"
          }
          overflow={"hidden"}
        >
          {flatFilesSelected
            ?.filter(
              (ele) =>
                ele?.s3ObjectKey?.length > 0 ||
                ele?.objectKey?.length! > 0 ||
                (cmsUpload && ele?.url?.length! > 0)
            )
            .map((ele: FileInfo) => (
              <VeBox
                sx={{
                  minWidth: wrapDocumentCards ? "300px" : "auto",
                  width:
                    wrapDocumentCards && !isMobile ? "calc(50% - 4px)" : "100%",
                }}
                key={ele?.s3ObjectKey || ele?.objectKey || ele?.url}
              >
                <UploadDocumentCard
                  showError={showError}
                  downloadOntextClick={downloadOntextClick}
                  item={ele}
                  image={image}
                  fileTypeImages={fileTypeImages}
                  setFileSelected={setFileSelected}
                  softDelete={softDelete}
                  edit={noPermission ? false : edit}
                  admin={admin}
                  multiple={mutliUpload}
                  setFileKey={setFileKey}
                  data={fileId ? data?.[fileId] : data}
                  isShareholder={isShareholder}
                  fileSizeRequired={cmsUpload ? false : fileSizeRequired}
                  downloadText={downloadText}
                  deleteFile={deleteFile}
                  showDeleteConfirmationModel={showDeleteConfirmationModel}
                  cmsUpload={cmsUpload}
                  deleteSuccess={deleteSuccess}
                  noPermission={noPermission}
                />
              </VeBox>
            ))}
        </VeBox>
      )}
      <VeBox>
        {showUploadedCard &&
          (wrapDocumentCards && !isMobile
            ? flatFilesSelected?.length > 4
            : flatFilesSelected?.length > 3) &&
          displayFiles &&
          (showAll ? (
            <VeBox
              sx={{ cursor: "pointer", marginTop: "4px" }}
              onClick={() => setShowAll(false)}
            >
              <VeTypography variant="label3Brand">See less</VeTypography>
            </VeBox>
          ) : (
            <VeBox sx={{ cursor: "pointer" }} onClick={() => setShowAll(true)}>
              <VeTypography variant="label3Brand">
                +{" "}
                {wrapDocumentCards && !isMobile
                  ? flatFilesSelected?.length - 4
                  : flatFilesSelected?.length - 3}{" "}
                more
              </VeTypography>
            </VeBox>
          ))}
      </VeBox>
      {showError && allFileSelected?.[0]?.commentStatus === "UNRESOLVED" && (
        <VeTypography
          variant="label3Tertiary"
          sx={{ color: "#DE1135 !important", marginTop: "10px" }}
        >
          <ul>
            {allFileSelected?.[0]?.comment?.split("\n")?.map((item: any) => {
              if (item !== "") {
                return (
                  <li style={{ listStyle: "none", display: "block" }}>
                    {!item.includes("-") ? `- ${item}` : `${item}`}
                  </li>
                );
              }
            })}
          </ul>
        </VeTypography>
      )}
    </VeBox>
  );
}
